import { JobAction } from "~/interface/Job"

// Item-level duplicate guard for incoming mobile orders (pos.mobile-v1 and
// pos.mobile-v2). Answers "has this exact order line already been taken today?"
// from the WHOLE day's jobchain, not from what is still on the table.
//
// Why the table is not enough: once a bill is paid its lines leave the table, so
// a phone retry that lands after payment looked brand new — it was re-added,
// stamped with a later pcTimestamp, replayed AFTER the Pay and reopened a closed
// bill (shop 5224, T22, Leo x2, 2026-10-28). The jobchain keeps every ADD until
// Close Shop and is restored from RTDB on reload, so this index survives a
// reload where the old in-memory sets did not.
//
// Key = `${alias}_${item.uniqueid}`. The item uniqueid is the phone's epoch
// millis, so two phones can collide on different tables in the same ms — the
// alias keeps those apart; the same table cannot repeat it. The alias is taken
// from the ADD job (the table the line was ordered on), never from where the
// line sits now, so a move to another table does not hide it from a retry that
// still carries the original alias.

// Must mirror JobBuilderV1.withTableAlias — the alias every job is stored under.
const normaliseAlias = (rawAlias: any) =>
    String(rawAlias || '').replace('โต๊ะ ', 'T').replace('Table ', 'T')

export const mobileItemKey = (rawAlias: any, itemUniqueid: any) =>
    `${normaliseAlias(rawAlias)}_${itemUniqueid}`

const parseOrderItems = (json: any): any[] => {
    try {
        const order = typeof json === 'string' ? JSON.parse(json) : json
        return Array.isArray(order?.orderitems) ? order.orderitems : []
    } catch (e) {
        return []
    }
}

export function createMobileItemIndex() {
    // Built incrementally: each jobchain key is parsed once, so a lookup costs a
    // key scan plus parsing only the jobs that arrived since the last order.
    let source: object | null = null
    const indexedJobKeys = new Set<string>()
    const itemToJobKey = new Map<string, string>()

    const sync = (jobs: Record<string, any>) => {
        // A replaced jobs object (store switch / restore) starts a fresh index.
        if (jobs !== source) {
            source = jobs
            indexedJobKeys.clear()
            itemToJobKey.clear()
        }
        for (const jobKey of Object.keys(jobs || {})) {
            if (indexedJobKeys.has(jobKey)) continue
            indexedJobKeys.add(jobKey)

            const job = jobs[jobKey]
            if (Number(job?.type) !== JobAction.ADD || !job.json) continue
            for (const item of parseOrderItems(job.json)) {
                if (!item?.uniqueid) continue
                const key = mobileItemKey(job.alias, item.uniqueid)
                if (!itemToJobKey.has(key)) itemToJobKey.set(key, jobKey)
            }
        }
    }

    // Splits an incoming order's lines into the ones never taken (`fresh`) and
    // the ones already in the jobchain. `matchedJobKey` is the jobchain key of
    // the first already-taken line, so a full duplicate can be acked with the
    // original job's id. Lines without a uniqueid cannot be matched and always
    // count as fresh — same as before this guard existed.
    const partition = (jobs: Record<string, any>, rawAlias: any, orderItems: any[]) => {
        sync(jobs)
        const fresh: any[] = []
        let matchedJobKey: string | undefined
        for (const item of orderItems || []) {
            const jobKey = item?.uniqueid
                ? itemToJobKey.get(mobileItemKey(rawAlias, item.uniqueid))
                : undefined
            if (jobKey) {
                matchedJobKey ??= jobKey
            } else {
                fresh.push(item)
            }
        }
        return { fresh, matchedJobKey }
    }

    return { partition }
}

// One index for both mobile intakes — they read the same jobchain.
export const mobileItemIndex = createMobileItemIndex()
